import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectorRef,
  Component,
  output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { APP_CONFIG, filterBoolean } from '@core/common-legacy';
import { HttpApiService } from '@core/http-legacy';
import { I18nService } from '@core/i18n-legacy';
import { SlackIntegrationActions, SlackIntegrationSelectors } from '@core/slack-integration-data';
import { DialogService } from '@core/user-interface-legacy';

import { IntegrationLegalDialogService } from '../../misc/integration-list-legal-dialog/integration-legal-dialog.service';
import { SlackIntegrationComponent } from '../slack-integration/slack-integration.component';

@Component({
  selector: 'rt-slack-container',
  templateUrl: './slack-container.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SlackIntegrationComponent],
})
export class SlackContainerComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly dialogService = inject(DialogService);
  private readonly integrationLegalDialogService = inject(IntegrationLegalDialogService);
  private readonly appConfig = inject<{
    authUrl: string;
  }>(APP_CONFIG);
  protected readonly httpApi = inject(HttpApiService);

  public hasSlackIntegrations: boolean = false;
  public slackIntegrationLoaded: boolean = false;

  public integrationUpdated = output<void>();

  public removeActionConfig = {
    handler: () => {
      this.disconnectAllSlackIntegrations();
    },
    dialogConfig: {
      title: this.i18n.translate('app.settings.integrations.slack.remove_dialog.title'),
      variant: 'error',
      agreeLabel: this.i18n.translate('app.settings.integrations.slack.remove_dialog.agree_label'),
      message: this.i18n.translate(
        'app.settings.integrations.slack.marketplace_remove_dialog.message',
      ),
      safeFooter: true,
      cancelLabel: this.i18n.translate('rti.common.cancel'),
    },
  };

  private ngOnDestroy$ = new Subject<void>();

  public ngOnInit(): void {
    this.store
      .select(SlackIntegrationSelectors.isLoaded())
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(loaded => {
        this.slackIntegrationLoaded = loaded;
        this.cdRef.detectChanges();
      });

    this.store
      .select(SlackIntegrationSelectors.allEntities)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(data => {
        this.hasSlackIntegrations = data.length > 0;
        this.cdRef.detectChanges();
      });

    this.store.dispatch(SlackIntegrationActions.fetchCollection());
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public integrate(): void {
    this.integrationLegalDialogService
      .openLegalDialog(
        null,
        this.i18n.translate('app.settings.integrations.slack.name'),
        undefined,
        {
          helpArticleUrl: this.i18n.translate('app.settings.integrations.slack.help_article_link'),
        },
      )
      .subscribe(result => {
        if (result && result.accepted) {
          this.newSlackIntegration();
        }
      });
  }

  public remove(): void {
    this.dialogService.warning(this.removeActionConfig.dialogConfig).subscribe(confirmed => {
      if (!confirmed) return;
      this.removeActionConfig.handler();
    });
  }

  public newSlackIntegration(): void {
    window.location.href = `${this.appConfig.authUrl}c/${this.httpApi.currentCompanyId}/integrations?integration=slack`;
  }

  public disconnectAllSlackIntegrations(): void {
    this.store.dispatch(SlackIntegrationActions.disconnectIntegrationCollection());
  }
}
